import React, { useCallback, useEffect, useState } from 'react';
import AppHeader from '../../component/AppHeader/app-header';
import { useNavigate } from 'react-router-dom';
import './PaymentSuccess.scss';
import { axiosInstanceV2 } from '../../services/axios';
import { useSelector } from 'react-redux';
import Button from '../../component/ui/Button';
import PageLoader from '../../component/PageLoader';
import useSubUrl from '../../hooks/useSubUrl';

const PaymentSuccessPage = () => {
	const navigate = useNavigate();
	const [loading, setLoading] = useState(true);
	const [data, setData] = useState(null);
	const user = useSelector((state) => state?.userInfo);
	const isOnboardingCompleted = user?.isOnboardingCompleted;
	const isOrganisationSetuped = user?.userInfo?.data?.is_organisation_setup;
	// Every other authenticated URL in the app is built from this
	// (localStorage 'suburl', set once onboarding completes) — this page was
	// the one place reading it off Redux instead, which isn't guaranteed to
	// be populated yet when this page renders (e.g. right after payment,
	// before any fetchUserInfo() call resolves), producing
	// /user/undefined/settings/my-subscription. Fall back to the Redux value
	// only if localStorage genuinely doesn't have it yet.
	const suburl = useSubUrl();
	const subDomain = suburl || user?.userInfo?.data?.sub_domain;

	const [refreshing, setRefreshing] = useState(false);

	const fetchPayments = useCallback(async () => {
		try {
			const authToken = localStorage.getItem('authToken');
			const response = await axiosInstanceV2.get('/subscription/payment-success', {
				headers: { Authorization: `Bearer ${authToken}` },
			});
			setData(response.data.data);
			return response.data.data;
		} catch (err) {
			console.error(err);
			return null;
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		fetchPayments();
	}, [fetchPayments]);

	// Razorpay confirms a charge asynchronously, so a subscription can still be
	// 'created'/'authenticated' for a few seconds after the user lands here.
	// Re-check a handful of times so it resolves on its own rather than leaving
	// the user staring at a status that looks like a failure.
	useEffect(() => {
		const settled = ['active', 'completed', 'cancelled', 'halted'];
		if (!data?.status || settled.includes(String(data.status).toLowerCase())) return;

		let attempts = 0;
		const timer = setInterval(async () => {
			attempts += 1;
			const fresh = await fetchPayments();
			const status = String(fresh?.status ?? '').toLowerCase();
			if (attempts >= 5 || settled.includes(status)) clearInterval(timer);
		}, 3000);

		return () => clearInterval(timer);
	}, [data?.status, fetchPayments]);

	const handleManualRefresh = async () => {
		setRefreshing(true);
		try {
			await fetchPayments();
		} finally {
			setRefreshing(false);
		}
	};

	if (loading) {
		return <PageLoader />;
	}

	if (!data) {
		return (
			<div className="payment-success-container">
				<div className="payment-card">No payment data found.</div>
			</div>
		);
	}

	// destructure response
	const {
		plan_name,
		plan_key,
		billing_period,
		amount,
		currency,
		subscription_id,
		status,
		is_recurring,
		total_count,
		paid_count,
		current_start,
		current_end,
		next_billing_date,
		payment_id,
		order_id,
		invoice_id,
		invoice_pdf_url,
		payment_details,
		invoice_details,
		sub_total,
		tax_amount,
		total,
		tax_percentage,
	} = data;

	// format helpers
	const formatDate = (date) =>
		date
			? new Date(date).toLocaleDateString('en-IN', {
					day: '2-digit',
					month: 'short',
					year: 'numeric',
				})
			: '-';
	const formatMoney = (amt) => `${currency ?? 'INR'} ₹${(amt ?? 0).toFixed(2)}`;

	// 'created' / 'authenticated' mean Razorpay has taken the payment but hasn't
	// finished settling the subscription yet.
	const isAwaitingConfirmation = !['active', 'completed', 'cancelled', 'halted'].includes(
		String(status ?? '').toLowerCase(),
	);

	// payment method display
	const paymentMethodLabel = () => {
		if (payment_details?.method === 'upi') return `UPI (${payment_details.upi?.vpa ?? '-'})`;
		if (payment_details?.method === 'card')
			return `${payment_details.card?.network} ****${payment_details.card?.last4}`;
		return payment_details?.method ?? '-';
	};

	return (
		<>
			<AppHeader title="Payment Status" />
			<main className="main-wrapper">
				<div className="payment-success-container">
					<div className="payment-card">
						{/* ── HEADER ────────────────────────────────────────────── */}
						<div className="payment-header">
							<div className="success-icon">✔</div>
							<div>
								<p className="order-id">ORDER #{order_id ?? subscription_id ?? '-'}</p>
								<h3>Payment Successful</h3>
								<p className="email-text">Confirmation sent to {payment_details?.email ?? '-'}</p>
							</div>
						</div>

						{/* Razorpay confirms the charge asynchronously, so the status can
						    briefly read 'created'/'authenticated'. Without an explanation
						    that looks like the payment failed. */}
						{isAwaitingConfirmation && (
							<div
								className="mb-3 rounded-3 p-3"
								style={{ background: '#EEF6FF', border: '1px solid #6EA8FE' }}
							>
								<div className="d-flex align-items-center gap-2 flex-wrap">
									<span style={{ fontSize: 18 }}>⏳</span>
									<strong className="text-dark">Confirming your payment…</strong>
								</div>
								<div className="mt-2 fs-5">
									Your payment went through. We're waiting for the provider to confirm it, which
									usually takes a few seconds. This page updates on its own — no need to pay again.
								</div>
								<button
									type="button"
									className="btn btn-link p-0 mt-2 fs-5"
									onClick={handleManualRefresh}
									disabled={refreshing}
								>
									{refreshing ? 'Checking…' : 'Check now'}
								</button>
							</div>
						)}

						{/* ── BODY ──────────────────────────────────────────────── */}
						<div className="payment-body">
							{/* LEFT — Plan Details + Next Steps */}
							<div className="plan-details">
								<div className="plan-card">
									<div className="plan-header">
										<h5>Plan Details</h5>
										<span className={`badge badge-${status}`}>{status}</span>
									</div>

									<div className="plan-content">
										<div>
											<strong>{plan_name ?? plan_key ?? 'Plan'}</strong>
											<p>
												{billing_period === 'monthly' ? 'Monthly' : 'Yearly'} Plan
												{is_recurring ? ' • Auto-renewal ON' : ' • One-time'}
											</p>
											<p className="valid">
												Valid: {formatDate(current_start)} – {formatDate(current_end)}
											</p>
											{is_recurring && next_billing_date && (
												<p className="next-billing">
													Next billing: {formatDate(next_billing_date)}
												</p>
											)}
											<p className="cycles">
												Cycles: {paid_count} / {total_count}
											</p>
										</div>
										<span className="price">{formatMoney(amount)}</span>
									</div>
								</div>

								{/* Payment Method */}
								<div className="payment-method-box mt-3">
									<h6>Payment Method</h6>
									<p>{paymentMethodLabel()}</p>
									{payment_details?.captured_at && (
										<p
											className="text-muted"
											style={{ fontSize: 12 }}
										>
											Paid on {formatDate(payment_details.captured_at)}
										</p>
									)}
								</div>

								{/* Next Steps */}
								{!isOnboardingCompleted && !isOrganisationSetuped && (
									<div>
										<h6 className="mt-4">What's Next?</h6>
										<div className="steps">
											<div className="step-box">
												<span>1</span>
												<div>
													<strong>Setup Organization</strong>
													<p>Configure your business & GST info</p>
												</div>
											</div>
											<div className="step-box">
												<span>2</span>
												<div>
													<strong>Setup Inventory</strong>
													<p>Add products and manage your stock</p>
												</div>
											</div>
											<div className="step-box">
												<span>3</span>
												<div>
													<strong>Start Creating Invoices</strong>
													<p>Create your first invoice and send to customers</p>
												</div>
											</div>
										</div>
									</div>
								)}

								<div className="d-flex justify-content-center mt-4 align-items-center">
									{isOnboardingCompleted && isOrganisationSetuped ? (
										<Button
											className="btn btn-primary"
											onClick={() => navigate(`/user/${subDomain}/settings/my-subscription`)}
											button="Go to Subscription"
										/>
									) : (
										<Button
											className="btn btn-primary mt-4"
											onClick={() => navigate(`/organizationDetail`)}
											button="Complete Organisation Setup"
										/>
									)}
								</div>
							</div>

							{/* RIGHT — Order Summary */}
							<div className="order-summary-payment">
								<h5>Order Summary</h5>

								<div className="summary-row">
									<span>Subscription ID</span>
									<span style={{ fontSize: 11 }}>{subscription_id ?? '-'}</span>
								</div>

								<div className="summary-row">
									<span>Payment ID</span>
									<span style={{ fontSize: 11 }}>{payment_id ?? '-'}</span>
								</div>

								{invoice_id && (
									<div className="summary-row">
										<span>Invoice ID</span>
										<span style={{ fontSize: 11 }}>{invoice_id}</span>
									</div>
								)}

								{invoice_details && (
									<div className="summary-row">
										<span>Invoice No</span>
										<span>{invoice_details.invoice_number ?? '-'}</span>
									</div>
								)}

								<div className="summary-row">
									<span>Billing Period</span>
									<span style={{ textTransform: 'capitalize' }}>{billing_period}</span>
								</div>

								<hr className="border-2 border-dark opacity-50" />

								<div className="summary-row">
									<span>Sub Total</span>
									<span>{formatMoney(sub_total)}</span>
								</div>

								<div className="summary-row discount">
									<span>Discount</span>
									<span>-₹0.00</span>
								</div>

								<div className="summary-row">
									<span>GST ({tax_percentage}%)</span>
									<span>{formatMoney(tax_amount)}</span>
								</div>

								<hr className="border-2 border-dark opacity-50" />

								<div className="summary-row total">
									<span>Total Paid</span>
									<span>{formatMoney(total)}</span>
								</div>

								<p className="paid-with">Paid via {paymentMethodLabel()}</p>

								{/* Download Invoice */}
								{invoice_pdf_url ? (
									<a
										href={invoice_pdf_url}
										target="_blank"
										rel="noreferrer"
										className="btn-download"
									>
										Download Invoice
									</a>
								) : (
									<button
										className="btn-download"
										disabled
										title="Invoice will be available once payment is confirmed"
									>
										Invoice Pending
									</button>
								)}
							</div>
						</div>
					</div>
				</div>
			</main>
		</>
	);
};

export default PaymentSuccessPage;
